import { useState, useEffect, useCallback } from 'react';
import * as profileApi from '../../api/profileApi';
import * as pipelineApi from '../../api/pipelineApi';
import ClientProfileEditor from '../../components/profiles/ClientProfileEditor/ClientProfileEditor';
import RiskProfileEditor from '../../components/profiles/RiskProfileEditor/RiskProfileEditor';
import GoalProfileEditor from '../../components/profiles/GoalProfileEditor/GoalProfileEditor';
import AssetProfileEditor from '../../components/profiles/AssetProfileEditor/AssetProfileEditor';
import LiabilityProfileEditor from '../../components/profiles/LiabilityProfileEditor/LiabilityProfileEditor';
import PreferencesEditor from '../../components/profiles/PreferencesEditor/PreferencesEditor';
import ProfilePublishModal from '../../components/profiles/ProfilePublishModal/ProfilePublishModal';
import ProfileVersionCard from '../../components/profiles/ProfileVersionCard/ProfileVersionCard';
import './ProfileManager.css';

const PROFILE_TYPES = [
  { key: 'client', label: 'Client Profile', icon: '&#128100;' },
  { key: 'risk', label: 'Risk Profile', icon: '&#10067;' },
  { key: 'goal', label: 'Goal Profile', icon: '&#127919;' },
  { key: 'asset', label: 'Asset Profile', icon: '&#128200;' },
  { key: 'liability', label: 'Liability Profile', icon: '&#128203;' },
  { key: 'preferences', label: 'Preferences', icon: '&#9881;&#65039;' },
];

export default function ProfileManager() {
  const [activeTab, setActiveTab] = useState('builder');
  const [activeType, setActiveType] = useState('client');
  const [draft, setDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const draftData = await profileApi.getDraft();
      const versionsData = await profileApi.getVersions();
      setDraft(draftData);
      setVersions(versionsData);
      try {
        const pipelineDraft = await pipelineApi.getDraft();
        setStages([...(pipelineDraft.stages || [])].sort((a, b) => a.order - b.order));
      } catch (pipelineErr) {
        setStages([]);
      }
    } catch (err) {
      showToast('Failed to load profile config: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSaveClient(fields) {
    try {
      setSaving(true);
      await profileApi.updateClientProfile(fields);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Client profile saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveRisk(questions, scoreRanges) {
    try {
      setSaving(true);
      await profileApi.updateRiskProfile(questions, scoreRanges);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Risk profile saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveGoal(goalTemplates) {
    try {
      setSaving(true);
      await profileApi.updateGoalProfile(goalTemplates);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Goal profile saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAsset(assetCategories) {
    try {
      setSaving(true);
      await profileApi.updateAssetProfile(assetCategories);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Asset profile saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveLiability(liabilityCategories) {
    try {
      setSaving(true);
      await profileApi.updateLiabilityProfile(liabilityCategories);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Liability profile saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePreferences(preferenceTemplates) {
    try {
      setSaving(true);
      await profileApi.updatePreferences(preferenceTemplates);
      const draftData = await profileApi.getDraft();
      setDraft(draftData);
      showToast('Preferences saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(publishData) {
    try {
      setSaving(true);
      const newVersion = await profileApi.publishDraft(publishData);
      const versionsData = await profileApi.getVersions();
      setVersions(versionsData);
      setShowPublishModal(false);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
      showToast('Version v' + newVersion.version_number + ' Published!');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleActivateVersion(versionId) {
    if (!window.confirm('Restore this version? The current draft will be replaced.')) return;
    try {
      setSaving(true);
      await profileApi.activateVersion(versionId);
      const draftData = await profileApi.getDraft();
      const versionsData = await profileApi.getVersions();
      setDraft(draftData);
      setVersions(versionsData);
      showToast('Version activated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="pm"><div className="loading-state">Loading profile configuration...</div></div>;
  }

  const nextVersionNumber = (versions[0]?.version_number || 0) + 1;

  return (
    <div className="pm">
      {showBanner && (
        <div className="pm__achievement-banner">
          &#127881; Profile version published! Configuration is now locked.
        </div>
      )}

      <div className="pm__header">
        <h1 className="pm__title">Profile Manager</h1>
        <p className="pm__subtitle">Configure client information collection templates for all 6 profile types</p>
      </div>

      <div className="pm__tabs">
        <button
          className={`pm__tab ${activeTab === 'builder' ? 'pm__tab--active' : ''}`}
          onClick={() => setActiveTab('builder')}
        >
          Profile Builder
        </button>
        <button
          className={`pm__tab ${activeTab === 'history' ? 'pm__tab--active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Version History
        </button>
      </div>

      {activeTab === 'builder' && draft && (
        <div className="pm__builder-layout">
          <div className="pm__profile-sidebar">
            {PROFILE_TYPES.map(type => (
              <button
                key={type.key}
                className={`pm__profile-type-btn ${activeType === type.key ? 'pm__profile-type-btn--active' : ''}`}
                onClick={() => setActiveType(type.key)}
              >
                <span className="pm__profile-type-icon" dangerouslySetInnerHTML={{ __html: type.icon }} />
                {type.label}
              </button>
            ))}
          </div>

          <div className="pm__editor-panel">
            {activeType === 'client' && (
              <ClientProfileEditor
                fields={draft.client_profile_fields || []}
                stages={stages}
                onSave={handleSaveClient}
                saving={saving}
              />
            )}
            {activeType === 'risk' && (
              <RiskProfileEditor
                questions={draft.risk_questions || []}
                scoreRanges={draft.risk_score_ranges || []}
                stages={stages}
                onSave={handleSaveRisk}
                saving={saving}
              />
            )}
            {activeType === 'goal' && (
              <GoalProfileEditor
                goalTemplates={draft.goal_templates || []}
                stages={stages}
                onSave={handleSaveGoal}
                saving={saving}
              />
            )}
            {activeType === 'asset' && (
              <AssetProfileEditor
                assetCategories={draft.asset_categories || []}
                stages={stages}
                onSave={handleSaveAsset}
                saving={saving}
              />
            )}
            {activeType === 'liability' && (
              <LiabilityProfileEditor
                liabilityCategories={draft.liability_categories || []}
                stages={stages}
                onSave={handleSaveLiability}
                saving={saving}
              />
            )}
            {activeType === 'preferences' && (
              <PreferencesEditor
                preferenceTemplates={draft.preference_templates || []}
                stages={stages}
                onSave={handleSavePreferences}
                saving={saving}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <>
          <button
            className="btn btn--primary pm__add-btn"
            onClick={() => setShowPublishModal(true)}
          >
            + Publish New Version
          </button>

          <div className="pm__history-grid">
            {versions.map(version => (
              <ProfileVersionCard
                key={version._id}
                version={version}
                onActivate={handleActivateVersion}
                isActive={version.status === 'active'}
              />
            ))}
          </div>

          {versions.length === 0 && (
            <div className="pm__empty">
              No versions published yet. Configure your profile templates and publish the first version!
            </div>
          )}
        </>
      )}

      <ProfilePublishModal
        isOpen={showPublishModal}
        nextVersionNumber={nextVersionNumber}
        onPublish={handlePublish}
        onClose={() => setShowPublishModal(false)}
        isPublishing={saving}
      />

      {toast && (
        <div className={`pm__toast ${toast.type === 'error' ? 'pm__toast--error' : 'pm__toast--success'}`}>
          {toast.type === 'error' ? '\u26A0' : '\u2713'} {toast.msg}
        </div>
      )}
    </div>
  );
}
